<script setup lang="ts">
import { ChevronRight, Maximize, Minimize, Pause, Play, Sparkles, Volume2, VolumeX } from 'lucide-vue-next'
import type { FeedPreset, IRefVideoItem } from '../../types'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
const { t } = useI18n()

const props = defineProps<{
  isPaused: boolean
  canControl: boolean
  displayProgress: number
  currentTime: number
  duration: number
  isMuted: boolean
  isNativeFullscreen: boolean
  episodeIndex: number
  item: IRefVideoItem
  preset: FeedPreset
}>()

const emit = defineEmits<{
  (e: 'toggle-play'): void
  (e: 'toggle-mute'): void
  (e: 'toggle-fullscreen'): void
  (e: 'episodes-tap'): void
  (e: 'progress-click', event: MouseEvent): void
  (e: 'progress-mouse-down', event: MouseEvent): void
  (e: 'progress-touch-start', event: TouchEvent): void
  (e: 'progress-touch-move', event: TouchEvent): void
  (e: 'progress-touch-end'): void
  (e: 'primary-action'): void
}>()

const formatTime = (seconds?: number) => {
  if (!seconds || Number.isNaN(seconds)) return '00:00'
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const m = Math.floor(safeSeconds / 60)
  const s = safeSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const isRecommendMode = computed(() => props.preset === 'recommend')
const isCreationMode = computed(() => props.preset === 'creation')
</script>

<template>
  <div class="h-12 w-full relative z-100 bg-black flex flex-col justify-center">
    <!-- 进度条 -->
    <div
      class="h-0.5 hover:h-1 group w-full cursor-pointer duration-100 rounded-full bg-white/25 absolute -top-px hover:-top-0.5 inset-x-0 z-10"
      @click.stop="emit('progress-click', $event)"
      @mousedown.stop.prevent="emit('progress-mouse-down', $event)"
      @touchstart.stop.prevent="emit('progress-touch-start', $event)"
      @touchmove.stop.prevent="emit('progress-touch-move', $event)"
      @touchend.stop.prevent="emit('progress-touch-end')"
      @touchcancel.stop.prevent="emit('progress-touch-end')"
    >
      <div
        class="h-full bg-white/75 transition-[width] duration-75 ease-linear"
        :style="{ width: `${displayProgress}%` }"
      ></div>
      <div
        class="absolute invisible group-hover:visible top-1/2 z-20 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-sm shadow-white/60 transition-[left] duration-75 ease-linear"
        :style="{ left: `${displayProgress}%` }"
      ></div>
    </div>
    <!-- 标签 -->
    <div class="absolute top-0 w-5/6 -translate-y-full px-4 py-6 flex flex-col gap-4">
      <button
        v-if="!isCreationMode"
        type="button"
        @click.stop="emit('episodes-tap')"
        class="uppercase text-xl flex items-center gap-2 text-white hover:opacity-80 cursor-pointer text-left"
      >
        {{ item?.title }} <ChevronRight />
      </button>
      <!-- <div v-else class="uppercase text-xl flex items-center gap-2 text-white">{{ item?.title }}</div> -->

      <template v-if="isRecommendMode || isCreationMode">
        <div class="flex items-center gap-2">
          <span
            class="backdrop-blur-md bg-white/40 rounded-lg px-2 py-1 text-sm text-white"
            v-for="tag in item?.tags"
            :key="tag"
            >{{ tag }}</span
          >
        </div>
      </template>
      <div class="line-clamp-4 max-w-5/6 text-base leading-relaxed text-white/80" v-if="isRecommendMode">
        <span>{{ t('component.playletViewer.episode', { num: item.episodeIndex }) }}：</span> {{ item?.description }}
      </div>
    </div>

    <div class="flex items-center justify-between text-white px-4 text-sm">
      <div class="flex min-w-0 items-center gap-6">
        <button class="disabled:opacity-50" :disabled="!canControl" @click.stop="emit('toggle-play')">
          <Pause v-if="!isPaused" :size="20" class="fill-white" />
          <Play v-else :size="20" class="fill-white" />
        </button>

        <span class="flex items-center text-right text-sm text-white/80 tabular-nums">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </span>
        <span class="w-0.5 rounded-full h-3 bg-white/20"></span>
        <button
          v-if="!isCreationMode"
          class="shrink-0 flex items-center hover:opacity-80"
          @click.stop="emit('episodes-tap')"
        >
          {{ t('component.playletViewer.selectEpisode') }}：{{
            t('component.playletViewer.episode', { num: episodeIndex })
          }}
          <ChevronRight :size="20" class="inline-block ml-1" />
        </button>
        <button v-else type="button" class="shrink-0 flex items-center gap-1" @click.stop="emit('primary-action')">
          <Icon icon="solar:notes-minimalistic-bold-duotone" class="text-xl" />
          {{ t('component.feed.remixAction') }}
          <ChevronRight :size="20" class="inline-block ml-2" />
        </button>
      </div>

      <div class="ml-1 flex items-center gap-4">
        <button @click.stop="emit('toggle-mute')">
          <VolumeX v-if="isMuted" :size="20" />
          <Volume2 v-else :size="20" />
        </button>
        <button @click.stop="emit('toggle-fullscreen')">
          <Minimize v-if="isNativeFullscreen" :size="20" />
          <Maximize v-else :size="20" />
        </button>
      </div>
    </div>
  </div>
</template>
